/**
 * 注册邮箱验证页 — frame wHWmH @ ui/dogdisk-user.pen 页面-邮箱验证(注册流)。
 *
 * 路径:`/auth/email-verify?token={verify_token}`(注册后邮件链接)
 * 守卫:🌍 Public(注册后未登录;token 自带身份)
 *
 * 与 routes/profile/email-verify.tsx(tVtVl D2)区分:
 *  - tVtVl(改邮箱 confirm):已登录用户改邮箱新地址验证;走 /gapi/user/auth/change-email/confirm;
 *  - 本 frame(wHWmH 注册 verify):公开 token 落地页;走 /gapi/user/auth/verify-email。
 *
 * 状态机:
 *  - loading:URL 含 ?token,正在 POST verify-email
 *  - success:200 → 1.5s 后 navigate /auth/login(邀请用户登录;若 supabase auto-setSession 则跳 /)
 *  - error:无 ?token / 4xx → 显错误 + retry button(若有 token) + back-btn(跳 /auth/login)
 *
 * Spec:docs/superpowers/specs/2026-05-26-batch-d-user-routes-design.md §4.4
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient, ApiClientError } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

const F = 'wHWmH'

type Status = 'loading' | 'success' | 'error'

export function RegisterEmailVerifyPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token')
  const [status, setStatus] = useState<Status>(token ? 'loading' : 'error')
  const [errorMsg, setErrorMsg] = useState<string | null>(
    token ? null : '验证链接缺失 token,请重新申请注册验证邮件',
  )

  const doVerify = (t: string): void => {
    setStatus('loading')
    setErrorMsg(null)
    void (async () => {
      try {
        await apiClient.post('/gapi/user/auth/verify-email', { token: t })
        setStatus('success')
        // 1.5s 后跳 /auth/login(邀请用户输密码登录)
        setTimeout(() => navigate('/auth/login'), 1_500)
      } catch (err) {
        setStatus('error')
        const msg =
          err instanceof ApiClientError
            ? err.message || err.code
            : err instanceof Error
              ? err.message
              : '验证失败,请重试'
        setErrorMsg(msg)
      }
    })()
  }

  useEffect(() => {
    if (token) doVerify(token)
    // 仅初始化执行一次;retry 由 button 触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <MobileScreen>
      <div data-testid={F} className="flex flex-1 flex-col">
        <TopBar title="验证邮箱" fallback="/auth/login" />

        <div
          data-testid={`${F}-status`}
          data-status={status}
          className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
        >
          {status === 'loading' && (
            <>
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
              <p className="text-[15px] text-ink">正在验证邮箱…</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </div>
              <p className="text-[15px] font-semibold text-ink">邮箱已验证</p>
              <p className="text-[13px] text-ink-mute">即将跳转登录页…</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-100 text-danger-700">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </div>
              <p className="text-[15px] font-semibold text-ink">验证失败</p>
              {errorMsg && <p className="text-[13px] text-danger-700">{errorMsg}</p>}
              <div className="flex flex-col gap-3 self-stretch pt-4">
                {token && (
                  <button
                    type="button"
                    data-testid={`${F}-retry-btn`}
                    onClick={() => doVerify(token)}
                    className="h-11 rounded-lg bg-brand-500 text-[15px] font-semibold text-white active:bg-brand-600"
                  >
                    重试
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`${F}-back-btn`}
                  onClick={() => navigate('/auth/login')}
                  className="h-11 rounded-lg bg-surface-100 text-[15px] font-semibold text-ink active:bg-surface-200"
                >
                  返回登录
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </MobileScreen>
  )
}

export default RegisterEmailVerifyPage
